import React from 'react';
import axios from 'axios';
import Fuse from 'fuse.js';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { Typography } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import EnhancedTableToolbar from '../chekkit/EnhancedTableToolbar';
import EnhancedTableHead from '../chekkit/EnhancedTableHead';

function getSorting(order, orderBy) {
	if (
		orderBy === 'name' ||
		orderBy === 'address' ||
		orderBy === 'city' ||
		orderBy === 'province'
	) {
		return order === 'desc'
			? (a, b) => String(a[orderBy]).localeCompare(String(b[orderBy]))
			: (a, b) => String(b[orderBy]).localeCompare(String(a[orderBy]));
	}

	return order === 'desc'
		? (a, b) => b[orderBy] - a[orderBy]
		: (a, b) => a[orderBy] - b[orderBy];
}

const styles = (theme) => ({
	root: {
		width: '100%',
		marginTop: theme.spacing.unit * 3,
		marginBottom: theme.spacing.unit * 3,
		boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
		borderRadius: '12px',
		overflow: 'hidden',
		backgroundColor: '#ffffff',
	},
	table: {
		minWidth: 840,
		'& thead th': {
			fontWeight: 500,
			fontSize: '0.6875rem',
			color: theme.palette.text.secondary,
			backgroundColor: '#f8fafc',
			padding: '12px 16px',
			borderBottom: `2px solid ${theme.palette.grey[200]}`,
			textTransform: 'uppercase',
			letterSpacing: '0.03em',
			lineHeight: 1.1,
			whiteSpace: 'nowrap',
			'&:hover': {
				backgroundColor: '#f1f5f9',
			},
			'&:first-child': {
				paddingLeft: 24,
			},
		},
		'& tbody td, & tbody th': {
			padding: '12px 16px',
			fontSize: '0.875rem',
			color: theme.palette.text.secondary,
			borderBottom: `1px solid ${theme.palette.grey[100]}`,
			verticalAlign: 'middle',
			'&:first-child': {
				fontWeight: 500,
				color: theme.palette.text.primary,
				paddingLeft: 24,
			},
			'&[align="right"]': {
				paddingRight: 24,
			},
		},
		'@media (max-width: 1200px)': {
			// Hide Initial Rating + Total Requests
			'& thead th:nth-child(4), & thead th:nth-child(10)': {
				display: 'none',
			},
			'& tbody td:nth-child(4), & tbody th:nth-child(4), & tbody td:nth-child(10), & tbody th:nth-child(10)':
				{
					display: 'none',
				},
		},
		'@media (max-width: 992px)': {
			// Also hide New Reviews + Recent Requests
			'& thead th:nth-child(7), & thead th:nth-child(9)': {
				display: 'none',
			},
			'& tbody td:nth-child(7), & tbody th:nth-child(7), & tbody td:nth-child(9), & tbody th:nth-child(9)':
				{
					display: 'none',
				},
			'& thead th': {
				padding: '9px 12px',
			},
			'& tbody td, & tbody th': {
				padding: '10px 12px',
				verticalAlign: 'middle',
				'&:first-child': {
					paddingLeft: 16,
				},
				'&[align="right"]': {
					paddingRight: 16,
				},
			},
		},
		'@media (max-width: 768px)': {
			// Mobile: keep Location, Current Rating, Total Reviews, Growth %
			'& thead th:nth-child(2), & thead th:nth-child(3), & thead th:nth-child(6)':
				{
					display: 'none',
				},
			'& tbody td:nth-child(2), & tbody th:nth-child(2), & tbody td:nth-child(3), & tbody th:nth-child(3), & tbody td:nth-child(6), & tbody th:nth-child(6)':
				{
					display: 'none',
				},
		},
		'& tbody tr': {
			transition: 'all 0.2s ease',
			'&:hover': {
				backgroundColor: '#fafafa',
				transform: 'translateY(-1px)',
				boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
			},
		},
	},
	emptyCell: {
		color: theme.palette.grey[400],
		fontStyle: 'normal',
		fontSize: '0.875rem',
		textAlign: 'center',
	},
	numericCell: {
		fontFamily: theme.typography.fontFamilyMonospace,
		textAlign: 'right',
		fontFeatureSettings: '"tnum"',
		letterSpacing: '-0.01em',
	},
	percentagePositive: {
		color: '#c655d6',
		fontWeight: 600,
	},
	percentageNeutral: {
		color: theme.palette.text.secondary,
	},
	dateCell: {
		color: theme.palette.text.secondary,
		whiteSpace: 'nowrap',
		fontSize: '0.8125rem',
	},
	tableWrapper: {
		overflowX: 'auto',
		WebkitOverflowScrolling: 'touch',
		maxWidth: '100%',
		'&::-webkit-scrollbar': {
			height: '6px',
			width: '6px',
		},
		'&::-webkit-scrollbar-track': {
			background: '#f8fafc',
			borderRadius: '3px',
		},
		'&::-webkit-scrollbar-thumb': {
			background: theme.palette.grey[300],
			borderRadius: '3px',
			'&:hover': {
				background: theme.palette.grey[400],
			},
		},
	},
	button: {
		textTransform: 'none',
		fontWeight: 500,
		padding: '8px 20px',
		borderRadius: '8px',
		boxShadow: 'none',
		'&:hover': {
			boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
		},
	},
	searchField: {
		'& .MuiOutlinedInput-root': {
			borderRadius: '8px',
			'& fieldset': {
				borderColor: theme.palette.grey[200],
			},
			'&:hover fieldset': {
				borderColor: theme.palette.grey[300],
			},
		},
	},
	progress: {
		margin: theme.spacing.unit * 2,
		color: theme.palette.primary.main,
	},
	pagination: {
		borderTop: `1px solid ${theme.palette.grey[100]}`,
		padding: '16px 24px',
		'& .MuiTablePagination-select': {
			borderRadius: '6px',
			marginRight: theme.spacing.unit,
			marginLeft: theme.spacing.unit,
		},
	},
	exportContainer: {
		display: 'flex',
		alignItems: 'center',
		padding: '16px 24px',
		borderTop: '1px solid #f3f4f6',
		backgroundColor: '#f8fafc',
	},
	exportButton: {
		backgroundColor: '#c655d6',
		color: '#ffffff',
		minWidth: '120px',
		marginRight: theme.spacing.unit * 4,
		textTransform: 'none',
		fontWeight: 500,
		padding: '8px 20px',
		borderRadius: '8px',
		boxShadow: 'none',
		'&:hover': {
			backgroundColor: '#b94bc8',
			boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
		},
	},
	exportMessage: {
		color: theme.palette.text.secondary,
		fontSize: '0.875rem',
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing.unit,
	},
	downloadLink: {
		color: theme.palette.primary.main,
		textDecoration: 'none',
		fontWeight: 500,
		marginLeft: theme.spacing.unit,
		transition: 'all 0.2s ease',
		'&:hover': {
			color: theme.palette.primary.dark,
			textDecoration: 'underline',
		},
	},
	loadingContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing.unit,
	},
});

class EnhancedTable extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			order: 'asc',
			orderBy: 'address',
			selected: [],
			page: 0,
			rowsPerPage: 25,
			loading: true,
			allSelected: false,
			chekkitData: [],
			searchTerm: '',
			exportMessage: null,
			loadingExportData: false,
			downloadUrl: null,
		};
	}

	async componentDidMount() {
		this.getChekkitData();
	}

	async getChekkitData() {
		// get chekkit data object
		const res = await axios.get('/api/chekkit');
		const { chekkitData } = res.data;

		this.setState({
			chekkitData,
			loading: false,
		});
	}

	searchData = (event) => {
		const searchTerm = event.target.value;
		this.setState({ searchTerm });
	};

	exportData = async () => {
		this.setState({
			exportMessage: 'We are creating a csv file for you, please wait.',
			loadingExportData: true,
		});

		const res = await axios.get('/api/export-chekkit-data');

		const { data } = res;

		if (!data || data.error) {
			this.setState({
				exportMessage: 'There was an error exporting your data.',
				loadingExportData: false,
			});
		}

		const { downloadUrl } = data;

		return this.setState({
			exportMessage: 'Click the following link to download your data.',
			downloadUrl,
			loadingExportData: false,
		});
	};

	handleRequestSort = (event, property) => {
		const orderBy = property;
		let order = 'desc';

		if (this.state.orderBy === property && this.state.order === 'desc') {
			order = 'asc';
		}

		this.setState({ order, orderBy });
	};

	handleChangePage = (event, page) => {
		this.setState({ page });
	};

	handleChangeRowsPerPage = (event) => {
		this.setState({ rowsPerPage: event.target.value });
	};

	isSelected = (id) => this.state.selected.indexOf(id) !== -1;

	renderTableCell = (value, type) => {
		const { classes } = this.props;

		if (value === null || value === undefined || value === '') {
			return <Typography className={classes.emptyCell}>-</Typography>;
		}

		switch (type) {
			case 'numeric':
				return (
					<Typography className={classes.numericCell}>
						{value.toLocaleString()}
					</Typography>
				);
			case 'percentage':
				return (
					<Typography
						className={`${classes.numericCell} ${
							parseFloat(value) > 0
								? classes.percentagePositive
								: classes.percentageNeutral
						}`}
					>
						{value.replace(/%/g, '')}%
					</Typography>
				);
			case 'date':
				return (
					<Typography className={classes.dateCell}>
						{value}
					</Typography>
				);
			default:
				return <Typography>{value}</Typography>;
		}
	};

	renderTableBody = () => {
		const { chekkitData, order, orderBy, rowsPerPage, page } = this.state;

		return chekkitData
			.sort(getSorting(order, orderBy))
			.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
			.map((n) => (
				<TableRow tabIndex={-1} key={n._business}>
					<TableCell component='th' scope='row'>
						{this.renderTableCell(n.name, 'text')}
					</TableCell>
					<TableCell>
						{this.renderTableCell(
							n.messagesThisBillingPeriod,
							'numeric'
						)}
					</TableCell>
					<TableCell>
						{this.renderTableCell(n.lastUsedDate, 'date')}
					</TableCell>
					<TableCell>
						{this.renderTableCell(n.initialRating, 'numeric')}
					</TableCell>
					<TableCell>
						{this.renderTableCell(n.starRating, 'numeric')}
					</TableCell>
					<TableCell>
						{this.renderTableCell(n.totalReviews, 'numeric')}
					</TableCell>
					<TableCell>
						{this.renderTableCell(n.newReviews, 'numeric')}
					</TableCell>
					<TableCell>
						{this.renderTableCell(n.percentIncrease, 'percentage')}
					</TableCell>
					<TableCell>
						{this.renderTableCell(n.rrLast30Days, 'numeric')}
					</TableCell>
					<TableCell>
						{this.renderTableCell(n.rrTotal, 'numeric')}
					</TableCell>
				</TableRow>
			));
	};

	renderSearchedTableBody = () => {
		const { chekkitData, order, orderBy, rowsPerPage, page, searchTerm } =
			this.state;

		const options = {
			shouldSort: true,
			threshold: 0.3,
			location: 0,
			distance: 10,
			maxPatternLength: 32,
			minMatchCharLength: 2,
			keys: ['name'],
		};

		const searchedData = new Fuse(chekkitData, options);

		return searchedData
			.search(searchTerm)
			.sort(getSorting(order, orderBy))
			.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
			.map((n) => (
				<TableRow tabIndex={-1} key={n._business}>
					<TableCell component='th' scope='row'>
						{this.renderTableCell(n.name, 'text')}
					</TableCell>
					<TableCell>
						{this.renderTableCell(
							n.messagesThisBillingPeriod,
							'numeric'
						)}
					</TableCell>
					<TableCell>
						{this.renderTableCell(n.lastUsedDate, 'date')}
					</TableCell>
					<TableCell>
						{this.renderTableCell(n.initialRating, 'numeric')}
					</TableCell>
					<TableCell>
						{this.renderTableCell(n.starRating, 'numeric')}
					</TableCell>
					<TableCell>
						{this.renderTableCell(n.totalReviews, 'numeric')}
					</TableCell>
					<TableCell>
						{this.renderTableCell(n.newReviews, 'numeric')}
					</TableCell>
					<TableCell>
						{this.renderTableCell(n.percentIncrease, 'percentage')}
					</TableCell>
					<TableCell>
						{this.renderTableCell(n.rrLast30Days, 'numeric')}
					</TableCell>
					<TableCell>
						{this.renderTableCell(n.rrTotal, 'numeric')}
					</TableCell>
				</TableRow>
			));
	};

	renderSkeletonRow = (rowIdx) => {
		const skeletonStyle = {
			background:
				'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%)',
			borderRadius: 4,
			minHeight: 24,
			height: 24,
			animation: 'skeleton-loading 1.2s infinite',
			backgroundSize: '400% 100%',
			margin: 0,
		};
		// Using rowIdx as key is acceptable for loading skeletons
		// eslint-disable-next-line react/no-array-index-key
		return (
			<TableRow key={`skeleton-${rowIdx}`}>
				{[...Array(10)].map((_, colIdx) => (
					// eslint-disable-next-line react/no-array-index-key
					<TableCell key={`skeleton-cell-${rowIdx}-${colIdx}`}>
						<div style={skeletonStyle} />
					</TableCell>
				))}
			</TableRow>
		);
	};

	renderExportButton = (hasData = true) => {
		const { classes } = this.props;

		if (this.state.loadingExportData) {
			return (
				<div className={classes.exportContainer}>
					<div className={classes.loadingContainer}>
						<CircularProgress
							className={classes.progress}
							size={20}
						/>
						<Typography className={classes.exportMessage}>
							{this.state.exportMessage}
						</Typography>
					</div>
				</div>
			);
		}

		if (!hasData) return null;

		return (
			<div className={classes.exportContainer}>
				<Button
					size='medium'
					variant='contained'
					className={classes.exportButton}
					onClick={this.exportData}
				>
					Export Data
				</Button>
				{this.state.downloadUrl && (
					<Typography
						component='div'
						className={classes.exportMessage}
					>
						{this.state.exportMessage}
						<a
							href={this.state.downloadUrl}
							className={classes.downloadLink}
						>
							Download
						</a>
					</Typography>
				)}
			</div>
		);
	};

	renderEmptyStateRow = ({ title, subtitle }) => (
		<TableRow>
			<TableCell colSpan={10} style={{ padding: '28px 16px' }}>
				<div style={{ textAlign: 'center' }}>
					<Typography
						style={{
							fontWeight: 600,
							color: '#0f172a',
							marginBottom: 6,
						}}
					>
						{title}
					</Typography>
					<Typography style={{ color: '#64748b' }}>
						{subtitle}
					</Typography>
				</div>
			</TableCell>
		</TableRow>
	);

	render() {
		const { classes } = this.props;
		const {
			order,
			orderBy,
			selected,
			rowsPerPage,
			page,
			loading,
			allSelected,
			chekkitData,
			searchTerm,
		} = this.state;

		let tableBodyContent;
		if (loading) {
			tableBodyContent = Array.from({ length: 10 }).map((_, idx) =>
				this.renderSkeletonRow(idx)
			);
		} else if (searchTerm) {
			tableBodyContent = this.renderSearchedTableBody();
		} else {
			tableBodyContent = this.renderTableBody();
		}

		if (!loading && (!tableBodyContent || tableBodyContent.length === 0)) {
			if (searchTerm) {
				tableBodyContent = [
					this.renderEmptyStateRow({
						title: 'No results',
						subtitle: `No locations match "${searchTerm}". Try a different search.`,
					}),
				];
			} else {
				tableBodyContent = [
					this.renderEmptyStateRow({
						title: 'No Chekkit locations yet',
						subtitle:
							'Once you have Chekkit locations, they will show up here.',
					}),
				];
			}
		}

		return (
			<div>
				<Paper className={classes.root} elevation={0}>
					<EnhancedTableToolbar
						numSelected={selected.length}
						searchData={this.searchData}
						loading={loading}
					/>
					<div className={classes.tableWrapper}>
						<Table
							className={classes.table}
							aria-labelledby='tableTitle'
						>
							<EnhancedTableHead
								numSelected={selected.length}
								order={order}
								orderBy={orderBy}
								onRequestSort={this.handleRequestSort}
								rowCount={chekkitData.length}
								allSelected={allSelected}
							/>
							<TableBody>{tableBodyContent}</TableBody>
						</Table>
					</div>
					{this.renderExportButton(chekkitData.length > 0)}
					{chekkitData.length > 0 ? (
						<div className={classes.pagination}>
							<TablePagination
								component='div'
								count={chekkitData.length}
								rowsPerPage={rowsPerPage}
								page={page}
								backIconButtonProps={{
									'aria-label': 'Previous Page',
								}}
								nextIconButtonProps={{
									'aria-label': 'Next Page',
								}}
								onChangePage={this.handleChangePage}
								onChangeRowsPerPage={
									this.handleChangeRowsPerPage
								}
							/>
						</div>
					) : null}
				</Paper>
			</div>
		);
	}
}

export default withStyles(styles)(EnhancedTable);



// WEBPACK FOOTER //
// ./src/components/chekkit/ChekkitTable.js